import {
  HubSpotContactDTO,
  HubSpotContactPropertiesDTO,
} from "../../../api/src/services/hubspot-service";
import LoyaltyContext, {
  LoyaltyContextInterface,
} from "../../contexts/loyalty/context";
import { RequestParams, useRequest } from "../use-request";
import { useContext, useEffect, useMemo, useState } from "react";
import { useLocation } from "@reach/router";

import useAuth from "../../hooks/use-auth";
import {
  decodePreviewCode,
  hasPreviewPath,
} from "../../utils/decode-preview-code";
import isEqual from "lodash.isequal";

export interface LoyaltyInterface {
  isLoading: boolean;
  loyalty: HubSpotContactPropertiesDTO | null;
  showLoyalty?: boolean;
  error?: string;
}

type HookParams = {
  getTokenID: () => Promise<string>;
};

const API_URL = `/api/get-hubspot-contact`;

export const useLoyaltyContext = (): LoyaltyContextInterface =>
  useContext(LoyaltyContext);

export default useLoyaltyContext;

export const useLoyalty = (): LoyaltyInterface => {
  const { getTokenID } = useAuth();
  const location = useLocation();
  const isPreviewMode = hasPreviewPath(location.pathname);
  const isLoyaltyFeatureEnabled = process.env.GATSBY_LOYALTY_ENABLED === "true";
  const [loyalty, setLoyalty] = useState<HubSpotContactPropertiesDTO | null>(
    null
  );

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | undefined>(undefined);

  const request = isLoyaltyFeatureEnabled
    ? useRequest<HubSpotContactDTO>(createRequestParams({ getTokenID }))
    : null;

  const baseIndexName = process.env.GATSBY_ALGOLIA_OFFERS_INDEX ?? "";

  const isWineDetailsPage = () => {
    const pathRegex = /^\/wines\/[^/]+\/[^/]+\/?$/; // Matches /wines/{category}/{wineSlug}
    return pathRegex.test(location.pathname);
  };

  useEffect(() => {
    const validSorts = new Set(["price_asc", "price_desc"]);
    const getAlgoliaIndexName = (
      baseIndexName: string,
      locationSearch?: string
    ): string => {
      const params = new URLSearchParams(locationSearch ?? "");
      const sortBy = params.get("sortBy");

      if (sortBy && validSorts.has(sortBy)) {
        return `${baseIndexName}_${sortBy}`;
      }
      return baseIndexName;
    };

    const indexName = getAlgoliaIndexName(baseIndexName, location?.search);

    if (!isWineDetailsPage()) {
      localStorage.setItem("algoliaIndexName", indexName);
    }
  }, [location.search]);

  useEffect(() => {
    if (request) {
      setIsLoading(request.isLoading);
      if (request.data) {
        if (isPreviewMode) {
          const searchParams = new URLSearchParams(location.search);
          const code = searchParams.get("code");
          if (code) {
            const { tier } = decodePreviewCode(code);
            if (tier) {
              const newLoyalty = {
                ...request.data.properties,
                loyalty_tier: tier,
              };

              if (!isEqual(loyalty, newLoyalty)) {
                setLoyalty(newLoyalty);
              }
              return;
            }
          }
        } else if (!isEqual(loyalty, request.data.properties)) {
          setLoyalty(request.data.properties);
        }
      }
      if (request.error) {
        setError(request.error);
      }
    }
  }, [request]);

  const showLoyalty = useMemo(() => {
    if (!isLoyaltyFeatureEnabled || !loyalty) {
      return false;
    }

    const { loyalty_eligible_for_loyalty, loyalty_tier } = loyalty;
    return (
      loyalty_eligible_for_loyalty?.toString() === "true" &&
      loyalty_tier !== null
    );
  }, [isLoyaltyFeatureEnabled, loyalty]);

  return {
    isLoading,
    loyalty,
    showLoyalty,
    error,
  };
};

const createRequestParams = ({ getTokenID }: HookParams): RequestParams => ({
  url: API_URL,
  getTokenID,
});
